// Hindi of src/content/learn/situations.ts. Keep keys and structure in step with the English.
import type { ArticleBody } from './types';

const NHS = (path: string, label: string) => ({ label: `NHS, ${label}`, href: `https://www.nhs.uk/${path}` });
const AAP = (path: string, label: string) => ({ label: `American Academy of Pediatrics (HealthyChildren.org) ${label}`, href: `https://www.healthychildren.org/English/${path}` });

const DEV_PAED = 'बच्चों के विकास के विशेषज्ञ डॉक्टर (डेवलपमेंटल पीडियाट्रिशियन)';

export const SITUATIONS_HI: Record<string, ArticleBody> = {
  'late-talking': {
    intro: [
      'दादा-दादी, पड़ोसी और यहाँ तक कि डॉक्टर भी कह सकते हैं “जब तैयार होगा, बोलने लगेगा” और देर से बोलने वाले कुछ बच्चे सच में बराबरी पर आ जाते हैं। लेकिन कुछ नहीं आते, और बाहर से यह जानना नामुमकिन है कि कौन किसमें है। जाँच करवाने में ज़्यादा कुछ नहीं लगता; बहुत लंबा इंतज़ार करने से वे साल छूट सकते हैं जब मदद सबसे अच्छा काम करती है।',
    ],
    notice: [
      '18 महीने तक: लगभग 10 से कम शब्द, और आपको चीज़ें दिखाने के लिए उँगली से इशारा न करना।',
      '2 साल तक: दो शब्द जोड़कर न बोलना (“और दूध”, “पापा गए”)।',
      'समझ भी पीछे लगती है, बिना इशारों के आसान बातें न मान पाना।',
      'पहले बोले जाने वाले शब्दों में से किसी का भी छूट जाना।',
    ],
    helps: [
      'छोटे, आसान वाक्यों में साथ-साथ अपने दिन की बातें करते रहें।',
      'चीज़ें हाथ में उठाकर विकल्प दें: “केला या सेब?” और किसी भी कोशिश का इंतज़ार करें।',
      'उनकी आवाज़ों और इशारों की नक़ल करें, फिर एक शब्द जोड़ें।',
      'खेलते और खाते समय पीछे चल रहे टीवी और फ़ोन बंद रखें।',
    ],
    when: ['ऊपर दिया कोई भी संकेत, शब्दों का छूट जाना, या मन में बैठा ऐसा एहसास जो जाता ही नहीं। साथ में सुनने की जाँच भी करवाएँ।'],
    who: ['स्पीच और लैंग्वेज थेरेपिस्ट', 'ऑडियोलॉजिस्ट', DEV_PAED],
    nurture: 'घरेलू आकलन में लगभग 15–20 मिनट लगते हैं और यह दिखाता है कि आपके बच्चे की उम्र के हिसाब से बोलना और समझना कहाँ तक पहुँचा है, किसी विशेषज्ञ से उस बातचीत के लिए एक साफ़ शुरुआत।',
    sources: [AAP('ages-stages/toddler/Pages/Language-Delay.aspx', 'Language delay'), { label: 'ASHA; Speech and language development', href: 'https://www.asha.org/public/speech/development/' }],
  },

  'tantrums-meltdowns': {
    intro: [
      '**टैंट्रम (ज़िद वाला गुस्सा)** आमतौर पर कुछ पाने की चाह से जुड़ा होता है, और अक्सर तब कम हो जाता है जब बच्चे को दिखता है कि इससे बात नहीं बनेगी। **मेल्टडाउन** अलग है: बच्चे पर (शोर, थकान, बदलाव या भावनाओं का) बोझ हद से ज़्यादा हो जाता है और ख़ुद पर क़ाबू छूट जाता है। मेल्टडाउन जान-बूझकर नहीं होते, और इनमें सज़ा की नहीं, शांति की ज़रूरत होती है।',
    ],
    notice: [
      'ठीक पहले क्या हुआ था? भूख, थकान, कोई “ना”, स्क्रीन बंद करना, योजना में बदलाव, शोर वाली जगह।',
      'कितना बड़ा, और कितनी देर? क्या हफ़्तों के साथ यह बेहतर हो रहा है या बदतर?',
      'इसे ख़त्म करने में क्या मदद मिली? शांत जगह, गले लगाना, इंतज़ार, ध्यान बँटाना?',
    ],
    helps: [
      'उसी पल में: पास और शांत रहें, कम बोलें, उन्हें सुरक्षित रखें, और हो सके तो उन्हें किसी शांत जगह ले जाएँ।',
      'मेल्टडाउन के दौरान समझाने या सौदेबाज़ी की कोशिश न करें; इसके बारे में बाद में बात करें, जब सब शांत हों।',
      'एक काम से दूसरे पर जाने से पहले बता दें (“पाँच मिनट और, फिर जूते पहनने हैं”) और ऐसा टाइमर इस्तेमाल करें जो उन्हें दिखे।',
      'खाना और नींद नियमित रखें, बहुत से मुश्किल पलों के पीछे भूख और थकान होती है।',
      'शांत पलों पर ध्यान दें और उनकी तारीफ़ करें, और जब वे सहज हों तो शांत होने का एक आसान तरीक़ा सिखाएँ।',
    ],
    when: ['गुस्से के दौरे बहुत बार होते हैं, बहुत लंबे चलते हैं, या आपके बच्चे या दूसरों के लिए ख़तरनाक होते हैं।', 'ये 5 साल की उम्र के काफ़ी बाद तक बिना कम हुए चलते रहते हैं, या विकास से जुड़ी दूसरी चिंताओं के साथ आते हैं।'],
    who: ['बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)', DEV_PAED, 'ऑक्युपेशनल थेरेपिस्ट (अगर ट्रिगर संवेदी हों)'],
    nurture: 'ऐप में **एक पल लिखें** से मुश्किल पलों को लिखें, उनसे पहले क्या हुआ था और किस चीज़ से मदद मिली। कुछ हफ़्तों बाद पैटर्न किसी विशेषज्ञ को दिखाना आसान हो जाता है।',
    sources: [NHS('conditions/baby/babys-development/behaviour/temper-tantrums/', 'Temper tantrums'), AAP('family-life/family-dynamics/communication-discipline/Pages/default.aspx', 'Communication and discipline')],
  },

  'picky-eating': {
    intro: [
      'ज़्यादातर छोटे बच्चे ऐसे दौर से गुज़रते हैं जब वे कुछ ही चीज़ें खाते हैं। कुछ बच्चों में, अक्सर संवेदी अंतर या ऑटिज़्म वाले बच्चों में, यह सूची बहुत छोटी ही बनी रहती है, और नए खाने से सच में बहुत परेशानी हो सकती है। मक़सद है बिना लड़ाई-झगड़े के धीरे-धीरे सूची बढ़ाना।',
    ],
    notice: ['वे कितनी चीज़ें भरोसे से खा लेते हैं? लगभग 20 से कम हों तो किसी विशेषज्ञ को बताना सही है।', 'क्या टेक्सचर, रंग, गंध, तापमान या ब्रांड मायने रखता है?', 'क्या उनकी लंबाई और वज़न उम्मीद के मुताबिक़ बढ़ रहे हैं?'],
    helps: [
      'हर खाने में एक “सुरक्षित” चीज़ परोसें, साथ में किसी नई चीज़ का बहुत छोटा हिस्सा, उसे खाने का कोई दबाव न हो।',
      'उन्हें नए खाने को छूने, सूँघने, चाटने या पकाने में मदद करने दें; यह सब प्रगति ही है।',
      'सुरक्षित चीज़ों को बहुत छोटे क़दमों में बदलें (उसी रोटी का अलग आकार, फिर थोड़ा अलग आटा)।',
      'साथ बैठकर खाएँ, खाने का समय छोटा और सुखद रखें, और लालच देने या ज़बरदस्ती से बचें।',
    ],
    when: ['वज़न घटना या ठीक से न बढ़ना, खाने से उबकाई या उल्टी आना, या खाने की सूची छोटी होती जाना।'],
    who: ['बच्चों के डॉक्टर', 'ऑक्युपेशनल थेरेपिस्ट (खाने-पीने के लिए)', 'डाइटीशियन'],
    nurture: 'घरेलू प्रोग्राम में “खाने का समय” वाली जगह में खाने से जुड़ी गतिविधियाँ हैं, जो क़दम-दर-क़दम खाने के साथ सहजता बढ़ाने के लिए बनाई गई हैं।',
    sources: [NHS('conditions/baby/weaning-and-feeding/fussy-eaters/', 'Fussy eaters')],
  },

  'sleep-troubles': {
    intro: [
      'नींद की दिक्कतें सभी बच्चों में आम हैं, और ऑटिज़्म, ADHD या एंग्ज़ायटी वाले बच्चों में ख़ासतौर पर आम हैं। ख़राब नींद अगले दिन सब कुछ मुश्किल बना देती है, आपके बच्चे के लिए भी और आपके लिए भी।',
    ],
    notice: ['नींद आने में कितना समय लगता है, वे कितनी बार जागते हैं, और सुबह कितने बजे उठते हैं।', 'रात में खर्राटे, हाँफना या बीच-बीच में साँस रुकना, इनके बारे में डॉक्टर को बताएँ।', 'सोने से ठीक पहले स्क्रीन, मीठा या खेल।'],
    helps: [
      'हर दिन, वीकेंड और छुट्टियों में भी, सोने और जागने का एक ही समय रखें।',
      'एक ही क्रम में एक शांत, छोटी दिनचर्या बनाएँ: नहाना, नाइट-सूट पहनना, दाँत साफ़ करना, एक कहानी, बत्ती बंद।',
      'सोने से कम से कम एक घंटा पहले कोई स्क्रीन नहीं; कमरा अँधेरा, ठंडा और शांत रखें।',
      'हर दिन बाहर दिन की रोशनी में समय बिताएँ और शारीरिक गतिविधि करें।',
      'अगर वे रात में जाग जाएँ, तो सब कुछ उबाऊ रखें: शांत, अँधेरा और कम समय।',
    ],
    when: ['बीच-बीच में साँस रुकने के साथ खर्राटे, बहुत बार जागना, या नींद की ऐसी दिक्कतें जो दिन के व्यवहार या पढ़ाई पर असर डालें।'],
    who: ['बच्चों के डॉक्टर', DEV_PAED],
    nurture: '**एक पल लिखें** से रातें कैसी जा रही हैं, यह लिखते रहें; कुछ हफ़्तों का पैटर्न अगली विज़िट पर दिखाने में काम आता है।',
    sources: [AAP('healthy-living/sleep/Pages/default.aspx', 'Sleep')],
  },

  'toilet-training': {
    intro: [
      'बच्चे बहुत अलग-अलग उम्र में टॉयलेट ट्रेनिंग के लिए तैयार होते हैं। बहुत से बच्चे 2 से 3 साल के बीच तैयार हो जाते हैं; विकास में देरी या ऑटिज़्म वाले बच्चों को अक्सर ज़्यादा समय और ज़्यादा क़दम-दर-क़दम तरीक़े की ज़रूरत होती है। उम्र से ज़्यादा तैयारी मायने रखती है।',
    ],
    notice: ['एक बार में एक-दो घंटे तक सूखे रहना।', 'यह दिखाना कि उन्हें पता चलता है कि वे गीले हो गए हैं या पॉटी कर दी है।', 'थोड़ी देर बैठ पाना और कोई आसान निर्देश मान पाना।'],
    helps: [
      'बाथरूम की दीवार पर चरणों को क्रम से दिखाने वाली तस्वीरें लगाएँ।',
      'उन्हें तय समय पर ले जाएँ (जागने के बाद, खाने के बाद, बाहर जाने से पहले), बजाय यह पूछने के कि “जाना है?”',
      'माहौल शांत और सकारात्मक रखें; ग़लती से हो जाने पर कभी सज़ा न दें।',
      'हर बार, घर पर भी और दूसरे देखभाल करने वालों के साथ भी, एक जैसे शब्द और एक जैसी दिनचर्या इस्तेमाल करें।',
    ],
    when: ['कई महीनों की कोशिश के बाद भी कोई प्रगति नहीं, दर्द, क़ब्ज़, या सूखे रहने के बाद फिर से गीला करने लगना।'],
    who: ['बच्चों के डॉक्टर', 'ऑक्युपेशनल थेरेपिस्ट'],
    nurture: 'घरेलू आकलन का आत्मनिर्भरता वाला हिस्सा आपके बच्चे की उम्र के हिसाब से शौच और अपनी देखभाल को कवर करता है, और रिपोर्ट अगला क़दम सुझाती है।',
    sources: [NHS('conditions/baby/babys-development/potty-training-and-bedwetting/how-to-potty-train/', 'How to potty train'), AAP('ages-stages/toddler/toilet-training/Pages/default.aspx', 'Toilet training')],
  },

  'screen-time': {
    intro: [
      'स्क्रीन पारिवारिक जीवन का हिस्सा हैं। सबसे ज़्यादा मायने यह रखता है कि स्क्रीन किस चीज़ की जगह ले रही है, बातचीत, खेल, नींद और हिलना-डुलना, और क्या कोई बड़ा साथ में देख रहा है और बच्चे से बात कर रहा है।',
      'विश्व स्वास्थ्य संगठन की सलाह है कि 2 साल से कम उम्र के बच्चों के लिए बैठे-बैठे स्क्रीन देखने का कोई समय न हो, और 2 से 4 साल के बच्चों के लिए दिन में 1 घंटे से ज़्यादा नहीं, इससे कम हो तो और अच्छा।',
    ],
    helps: [
      'साथ मिलकर देखें और जो दिख रहा है उसके बारे में बात करें।',
      'खाने के समय, सोने के कमरे और सोने से पहले के एक घंटे में स्क्रीन न रखें।',
      'स्क्रीन टाइम को बस छीनने के बजाय किसी ख़ास चीज़ से बदलें (“दोपहर के खाने के बाद हम ब्लॉक्स से खेलेंगे”)।',
      'ऐसा टाइमर इस्तेमाल करें जो दिखे, और ख़त्म होने से पहले बता दें। पहले हफ़्ते विरोध की उम्मीद रखें, यह गुज़र जाता है।',
      'बच्चे के आसपास अपने स्क्रीन इस्तेमाल पर भी ध्यान दें।',
    ],
    myths: [{ myth: 'पढ़ाई वाले (एजुकेशनल) वीडियो छोटे बच्चों को बोलना सिखाते हैं।', fact: 'छोटे बच्चे भाषा मुख्य रूप से लोगों के साथ असली आपसी बातचीत से सीखते हैं, स्क्रीन से नहीं।' }],
    when: ['स्क्रीन टाइम की वजह से गंभीर झगड़े या नींद की दिक्कतें हो रही हैं, या आपके बच्चे की किसी और चीज़ में कोई दिलचस्पी नहीं है।'],
    who: ['बच्चों के डॉक्टर', 'बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)'],
    nurture: 'घरेलू प्रोग्राम आपको हर दिन के लिए एक छोटी, बिना स्क्रीन वाली गतिविधि देता है, जो घर में उसी जगह रखी गई है जहाँ वह अपने आप होती है।',
    sources: [{ label: 'World Health Organization; Guidelines on physical activity, sedentary behaviour and sleep for children under 5', href: 'https://www.who.int/news/item/24-04-2019-to-grow-up-healthy-children-need-to-sit-less-and-play-more' }, AAP('family-life/Media/Pages/default.aspx', 'Media')],
  },

  'school-refusal': {
    intro: [
      'कुछ बच्चे स्कूल जाने को लेकर बहुत परेशान हो जाते हैं, सोमवार सुबह पेट दर्द, गेट पर आँसू, या तैयार होने से मना करना। यह आमतौर पर इस बात का संकेत है कि स्कूल में कुछ बहुत मुश्किल लग रहा है: पढ़ाई, दोस्ती, शोर, कोई टीचर, बुलिंग (तंग किया जाना), या आपसे दूर होना।',
    ],
    notice: ['शारीरिक शिकायतें जो वीकेंड और छुट्टियों में ग़ायब हो जाती हैं।', 'चिंता जो रविवार शाम या छुट्टियों के बाद बढ़ने लगती है।', 'स्कूल को लेकर मूड, नींद या खाने में बदलाव।'],
    helps: [
      'पहले सुनें। पूछें कि दिन का सबसे मुश्किल हिस्सा कौन-सा है, और कब आसान लगता है।',
      'अगर किसी भी तरह हो सके तो स्कूल जाना जारी रखें, भले दिन के कुछ हिस्से के लिए, क्योंकि घर पर रहने से वापस जाना और मुश्किल हो जाता है।',
      'क्लास टीचर से मिलें, जानें कि वे क्या देखते हैं, और साथ मिलकर एक योजना तय करें।',
      'सुबह की शांत दिनचर्या और साफ़, छोटी-सी विदाई की योजना बनाएँ।',
    ],
    when: ['आपके बच्चे का स्कूल कुछ दिनों से ज़्यादा छूट चुका है, या आपको बुलिंग, सीखने की कठिनाई, एंग्ज़ायटी या उदासी (लो मूड) का शक है।'],
    who: ['स्कूल काउंसलर', 'बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)', 'स्पेशल एजुकेटर'],
    nurture: 'घरेलू आकलन दिखा सकता है कि स्कूल के साथ-साथ ध्यान, सीखने से जुड़े कौशल या मूड को भी देखना चाहिए या नहीं।',
    sources: [NHS('mental-health/children-and-young-adults/', 'Children and young people’s mental health')],
  },

  'teenage-mood': {
    intro: [
      'किशोर बहुत कुछ झेलते हैं, शरीर में बदलाव, परीक्षाएँ, दोस्ती, अपनी पहचान, सोशल मीडिया। मूड बदलना और ज़्यादा निजता चाहना सामान्य है। लेकिन उदासी, चिंता या सबसे दूर होना जो हफ़्तों तक चले और ज़िंदगी में रुकावट बने, उसे गंभीरता से लेना चाहिए।',
    ],
    notice: [
      'दो हफ़्ते या उससे ज़्यादा समय तक ज़्यादातर दिन उदासी, चिड़चिड़ापन या बुझापन रहना।',
      'दोस्तों और पहले पसंद आने वाली चीज़ों से दूर होना।',
      'नींद, खाने या स्कूल के प्रदर्शन में बड़े बदलाव।',
      'निराशा महसूस करने, ख़ुद को बोझ समझने, या ख़ुद को नुक़सान पहुँचाने की बातें करना।',
    ],
    helps: [
      'बात करने के लिए बिना दबाव वाले मौक़े ढूँढें, कार में, टहलते हुए, खाना बनाते समय, आमने-सामने से ज़्यादा आसान है साथ-साथ रहते हुए बात करना।',
      'सलाह देने से ज़्यादा सुनें। “यह सच में बहुत मुश्किल लगता है” जैसी बात बहुत काम करती है।',
      'कुछ दिनचर्याएँ साथ रखें: एक बार का खाना, एक सैर, कोई शो जो आप साथ देखते हैं।',
      'नींद, शारीरिक गतिविधि और दोस्तों के साथ समय बिताने के लिए प्रोत्साहित करें।',
    ],
    when: [
      'ख़ुद को नुक़सान पहुँचाने या आत्महत्या की कोई भी बात हो: तुरंत **Tele-MANAS 14416** (मुफ़्त, 24×7) पर कॉल करें, या अपने स्थानीय इमरजेंसी नंबर पर।',
      'उदासी या चिंता जो दो हफ़्ते से ज़्यादा चले, या स्कूल, दोस्ती या नींद पर असर डाले।',
    ],
    who: ['बच्चों और किशोरों के मनोचिकित्सक (साइकियाट्रिस्ट)', 'क्लिनिकल साइकोलॉजिस्ट', 'स्कूल काउंसलर'],
    nurture: '12 से 18 साल के बच्चों के घरेलू आकलन में मूड और मानसिक सेहत से जुड़े सवाल होते हैं। अगर किसी जवाब से लगे कि आपके किशोर बच्चे को ख़तरा हो सकता है, तो आपको तुरंत क्राइसिस हेल्पलाइन की जानकारी दिखेगी।',
    sources: [{ label: 'World Health Organization; Mental health of adolescents', href: 'https://www.who.int/news-room/fact-sheets/detail/adolescent-mental-health' }, AAP('ages-stages/teen/Pages/default.aspx', 'Teens'), { label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },
};
